import {
  useDeleteSculptureEditionMouvement,
  useDuplicateSculptureEditionMouvement,
  useGetSculptureEditionMouvements,
  useUpdateSculptureEditionMouvementSequence,
  type SculptureEditionMouvement,
} from "@/api/sculpture-edition-mouvement.api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddButton } from "@/components/ui/custom-buttons";
import { DataTable } from "@/components/ui/datatable";
import { toast } from "@/hooks/use-toast";
import { ColumnDef } from "@tanstack/react-table";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateOrEditSculptureEditionMouvement } from "./CreateOrEditSculptureEditionMouvement";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import { createActionsColumnDef } from "@/components/table/table-actions-column";

type SculptureEditionMouvementTableProps = {
  sculptureEditionId?: string;
  disabled?: boolean;
};

type FormMode = "create" | "edit" | "view";

export const SculptureEditionMouvementTable = ({
  sculptureEditionId,
  disabled = false,
}: SculptureEditionMouvementTableProps) => {
  const { t } = useTranslation();

  const { data = [] } = useGetSculptureEditionMouvements(sculptureEditionId);

  const [rows, setRows] = useState<SculptureEditionMouvement[]>([]);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>();

  const selectedMouvement = useRef<SculptureEditionMouvement>();

  const sequenceMutator = useUpdateSculptureEditionMouvementSequence();
  const deleter = useDeleteSculptureEditionMouvement();
  const duplicator = useDuplicateSculptureEditionMouvement();

  useEffect(() => {
    setRows(data);
  }, [data]);

  const openForm = (mode: FormMode, mouvement?: SculptureEditionMouvement) => {
    selectedMouvement.current = mouvement;
    setFormMode(mode);
  };

  const handleRowDragged = (newData: SculptureEditionMouvement[]) => {
    if (!sculptureEditionId) return;
    setRows(newData);
    sequenceMutator.mutate({
      newSequence: newData.map((d) => d.id),
      sculptureEditionId,
    });
  };

  const confirmDelete = () => {
    if (!deleteTargetId || !sculptureEditionId) return;
    deleter.mutate(
      { mouvementId: deleteTargetId, sculptureEditionId },
      {
        onSuccess: () => {
          toast({
            title: t("successDeleteSculptureEditionMouvement"),
            variant: "success",
          });
        },
        onError: () => {
          toast({
            title: t("errorDeleteSculptureEditionMouvement"),
            variant: "destructive",
          });
        },
        onSettled: () => setDeleteTargetId(undefined),
      },
    );
  };

  const handleDuplicate = useCallback(
    (mouvement: SculptureEditionMouvement) => {
      if (!sculptureEditionId) return;
      duplicator.mutate(
        { mouvementId: mouvement.id, sculptureEditionId },
        {
          onSuccess: () => {
            toast({
              title: t("successDuplicateSculptureEditionMouvement"),
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("errorDuplicateSculptureEditionMouvement"),
              variant: "destructive",
            });
          },
        },
      );
    },
    [duplicator, sculptureEditionId, t],
  );

  const datatableColumns = useMemo(
    (): ColumnDef<SculptureEditionMouvement>[] => [
      {
        size: 600,
        header: t("tab_sculpture.mouvement.description"),
        accessorKey: "description",
      },
      {
        header: t("tab_sculpture.mouvement.lieu"),
        accessorKey: "lieu",
      },
      {
        header: t("tab_sculpture.mouvement.dateDepart"),
        accessorKey: "dateDepart",
      },
      {
        header: t("tab_sculpture.mouvement.dateRetour"),
        accessorKey: "dateRetour",
      },
      createActionsColumnDef<SculptureEditionMouvement>(({ row }) => {
        const mouvement = row.original;
        const actions: TableRowAction[] = [
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            disabled: disabled,
            onSelect: () => openForm("edit", mouvement),
          },
          {
            id: "duplicate",
            label: t(ACTION_I18N_KEYS.duplicate),
            icon: Copy,
            disabled: disabled,
            onSelect: () => handleDuplicate(mouvement),
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            disabled: disabled,
            onSelect: () => setDeleteTargetId(mouvement.id),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ],
    [t, disabled, handleDuplicate],
  );

  if (!sculptureEditionId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <CreateOrEditSculptureEditionMouvement
        open={formMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormMode(null);
            selectedMouvement.current = undefined;
          }
        }}
        sculptureEditionId={sculptureEditionId}
        mouvement={
          formMode === "create" ? undefined : selectedMouvement.current
        }
        readOnly={formMode === "view"}
        disabled={disabled}
      />

      <AlertDialog
        open={deleteTargetId !== undefined}
        onOpenChange={(open) => !open && setDeleteTargetId(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-between items-baseline">
        <label className="text-sm font-medium text-gray-700">
          {t("sculpture.mouvement.tableLabel")}
        </label>
        <AddButton
          type="button"
          onClick={() => openForm("create")}
          disabled={disabled}
        />
      </div>

      <DataTable
        enableDragRows
        onRowDragged={(d) => handleRowDragged(d.newData)}
        columns={datatableColumns}
        data={rows}
      />
    </div>
  );
};

export default SculptureEditionMouvementTable;
